import { makeStyles, tokens, useId } from '@fluentui/react-components';
import { Toaster } from '@fluentui/react-toast';
import { Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { NuqsAdapter } from 'nuqs/adapters/react';
import { lazy } from 'react';

import type { RouterContext } from '../routerContext';

import { OfflineBanner } from '../components/OfflineBanner';
import { PWAUpdateToast } from '../components/PWAUpdateToast';
import { TopBar } from '../components/TopBar';
import { PageTitleProvider } from '../lib/context/PageTitleContext';
import { ToastProvider } from '../lib/toast/ToastProvider';

// Lazy load devtools only in development
const TanStackRouterDevtools =
  import.meta.env.VITE_ENV === 'development'
    ? lazy(() =>
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      )
    : () => null;

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  main: {
    flexGrow: 1,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
  },
});

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout(): React.ReactElement {
  const styles = useStyles();
  const toasterId = useId('toaster');

  return (
    <NuqsAdapter>
      <PageTitleProvider>
        <ToastProvider toasterId={toasterId}>
          <PWAUpdateToast toasterId={toasterId} />
          <Toaster toasterId={toasterId} />
          <div className={styles.root}>
            <TopBar />
            <OfflineBanner />
            <main className={styles.main}>
              <Outlet />
            </main>
          </div>
        </ToastProvider>
      </PageTitleProvider>
      <TanStackRouterDevtools />
    </NuqsAdapter>
  );
}
